import React, { createContext, useCallback, useContext, useReducer, useState } from 'react';
import { menuReducer } from '../reducer/menuReducer';

const MenuContext = createContext({});

export const MenuProvider = ({ children }) => {
  const [state, dispatch] = useReducer(menuReducer, {});
  const [resolvedMenuRoutes, setResolvedMenuRoutesState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('resolvedMenuRoutes')) ?? [];
    } catch {
      return [];
    }
  });

  const user = JSON.parse(localStorage.getItem('user'));

  const setMenus = (menus) => {
    return dispatch({ type: 'SET', payload: menus });
  };

  const getMenus = () => {
    return dispatch({ type: 'GET' });
  };

  const setResolvedMenuRoutes = useCallback((routes) => {
    const nextRoutes = Array.isArray(routes) ? routes : [];

    try {
      localStorage.setItem('resolvedMenuRoutes', JSON.stringify(nextRoutes));
    } catch {}

    setResolvedMenuRoutesState(nextRoutes);
    return nextRoutes;
  }, []);

  const getAllSubmenus = () => {
    const menus = Object.values(state);
    if (!menus || !Array.isArray(menus)) {
      return [];
    }

    return menus.reduce((acc, menu) => {
      if (menu.submenus) {
        return [...acc, ...menu.submenus];
      }
      return acc;
    }, []);
  };

  const getMenuSubMenuId = (url) => {
    const allSubmenus = getAllSubmenus();
    const normalizedUrl = normalizePath(url);
    const subMenu = allSubmenus.find((submenu) => normalizePath(submenu.url) === normalizedUrl);
    return subMenu?.subMenuId ?? null;
  };

  const getCanEdit = (submenuId) => {
    const submenus = getAllSubmenus();
    const subMenu = submenus.find((sm) => sm.subMenuId === submenuId);

    const permission = subMenu?.roleSubmenuPermission.find((permission) => permission.roleId === user.roleId);

    return permission ? permission.canEdit : false;
  };

  const getCanCopy = (submenuId) => {
    const submenus = getAllSubmenus();
    const subMenu = submenus.find((sm) => sm.subMenuId === submenuId);

    const permission = subMenu?.roleSubmenuPermission.find((permission) => permission.roleId === user.roleId);

    return permission ? permission.canCopy : false;
  };

  const getCanDelete = (submenuId) => {
    const submenus = getAllSubmenus();
    const subMenu = submenus.find((sm) => sm.subMenuId === submenuId);

    const permission = subMenu?.roleSubmenuPermission.find((permission) => permission.roleId === user.roleId);

    return permission ? permission.canDelete : false;
  };

  const getSubMenuNamesByUrls = (urls) => {
    const allSubmenus = getAllSubmenus();
    return urls.map((url) => {
      const subMenu = allSubmenus.find((submenu) => submenu.url === url);
      return subMenu?.subMenuName ?? null;
    });
  };

  const normalizePath = (path = '') => {
    return path
      .replace(/^\/app\/?/i, '')
      .replace(/^\/+|\/+$/g, '')
      .toLowerCase();
  };

  const getSubMenuNameByPath = (pathname) => {
    const normalizedPath = normalizePath(pathname);
    if (!normalizedPath) return null;

    const submenusSource = resolvedMenuRoutes.length > 0 ? resolvedMenuRoutes : getAllSubmenus();
    const normalizedSubmenus = submenusSource.map((submenu) => ({
      ...submenu,
      normalizedUrl: normalizePath(submenu.url),
    }));

    const exactMatch = normalizedSubmenus.find((submenu) => submenu.normalizedUrl === normalizedPath);
    if (exactMatch) {
      return exactMatch.subMenuName ?? null;
    }

    const prefixMatches = normalizedSubmenus
      .filter((submenu) => {
        if (!submenu.normalizedUrl) return false;
        return normalizedPath === submenu.normalizedUrl || normalizedPath.startsWith(`${submenu.normalizedUrl}/`);
      })
      .sort((a, b) => b.normalizedUrl.length - a.normalizedUrl.length);

    return prefixMatches[0]?.subMenuName ?? null;
  };

  const value = {
    menus: state,
    resolvedMenuRoutes,
    setMenus,
    setResolvedMenuRoutes,
    getMenus,
    getMenuSubMenuId,
    getSubMenuNamesByUrls,
    getSubMenuNameByPath,
    getCanEdit,
    getCanCopy,
    getCanDelete,
  };

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

const useMenus = () => {
  return useContext(MenuContext);
};

export default useMenus;
